'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import { ClipConfig, ReactionOutcome, classifyReaction, REACTION_WINDOW_MS } from '@/lib/clipConfigs';

// ── Types ─────────────────────────────────────────────────────────────────────

export type VideoReactMode = 'operator-react' | 'kill-react' | 'scenarios';

interface VideoReactPlayerProps {
  mode: VideoReactMode;
  clips: ClipConfig[];
  onClose?: () => void;
}

interface ShotRecord {
  id: number;
  outcome: ReactionOutcome;
  reactionMs: number | null;
  timestamp: number;
}

interface TriggerResult {
  triggerIndex: number;
  outcome: ReactionOutcome;
  reactionMs: number | null;
}

interface PlayerSettings {
  clipOrder: 'sequential' | 'shuffled';
  triggerOverrides: Record<string, number[]>;
}

const DEFAULT_SETTINGS: PlayerSettings = {
  clipOrder: 'sequential',
  triggerOverrides: {},
};

const SETTINGS_KEY_PREFIX = 'videoreact_settings_';

// ── Streamable URL resolver ───────────────────────────────────────────────────

function extractStreamableId(src: string): string | null {
  // Matches https://streamable.com/e/XXXXX or https://streamable.com/XXXXX
  const match = src.match(/streamable\.com\/(?:e\/)?([a-zA-Z0-9]+)/);
  return match ? match[1] : null;
}

async function resolveStreamableUrl(src: string): Promise<string> {
  const id = extractStreamableId(src);
  if (!id) return src; // not a streamable URL, return as-is
  try {
    const res = await fetch(`https://api.streamable.com/videos/${id}`);
    if (!res.ok) throw new Error('API error');
    const data = await res.json();
    // Prefer full mp4 for best quality, fall back to mp4-mobile
    const url =
      data?.files?.mp4?.url ||
      data?.files?.['mp4-mobile']?.url ||
      null;
    if (url) {
      // Streamable returns protocol-relative URLs sometimes
      return url.startsWith('//') ? `https:${url}` : url;
    }
  } catch {
    // fall through
  }
  return src;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function getModeAccent(mode: VideoReactMode) {
  if (mode === 'operator-react') return '#ff4655';
  if (mode === 'kill-react') return '#b44fff';
  return '#00e5ff';
}

function getModeLabel(mode: VideoReactMode) {
  if (mode === 'operator-react') return 'Kill Scenarios';
  if (mode === 'kill-react') return 'Kill React';
  return 'Esport Scenarios';
}

function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function outcomeColor(outcome: ReactionOutcome, accent: string): string {
  if (outcome === 'too-early' || outcome === 'too-late') return '#ff4655';
  if (outcome === 'perfect') return accent;
  if (outcome === 'good') return '#00e676';
  return 'rgba(255,255,255,0.6)';
}

function outcomeLabel(outcome: ReactionOutcome): string {
  if (outcome === 'too-early') return 'Early';
  if (outcome === 'too-late') return 'Late';
  if (outcome === 'perfect') return 'Perfect';
  if (outcome === 'good') return 'Good';
  return 'Okay';
}

// ── Top Stats Bar ─────────────────────────────────────────────────────────────

interface StatsBarProps {
  shots: ShotRecord[];
  accentColor: string;
  totalTriggers: number;
  currentTrigger: number;
}

function StatsBar({ shots, accentColor, totalTriggers, currentTrigger }: StatsBarProps) {
  const hits = shots.filter(s => s.outcome !== 'too-early' && s.outcome !== 'too-late');
  const misses = shots.filter(s => s.outcome === 'too-early' || s.outcome === 'too-late');
  const reactionTimes = hits.filter(s => s.reactionMs !== null).map(s => s.reactionMs as number);
  const avgMs = reactionTimes.length > 0 ? Math.round(reactionTimes.reduce((a, b) => a + b, 0) / reactionTimes.length) : null;
  const bestMs = reactionTimes.length > 0 ? Math.min(...reactionTimes) : null;
  const worstMs = reactionTimes.length > 0 ? Math.max(...reactionTimes) : null;

  let streak = 0;
  for (let i = shots.length - 1; i >= 0; i--) {
    if (shots[i].outcome !== 'too-early' && shots[i].outcome !== 'too-late') streak++;
    else break;
  }

  const lastShot = shots[shots.length - 1] ?? null;

  return (
    <div
      className="absolute top-0 left-0 right-0 z-40 flex items-center gap-0 pointer-events-none"
      style={{
        background: 'linear-gradient(to bottom, rgba(8,8,12,0.92) 0%, rgba(8,8,12,0.7) 80%, transparent 100%)',
        paddingTop: 8,
        paddingBottom: 16,
        paddingLeft: 12,
        paddingRight: 12,
      }}
    >
      <div className="flex items-center gap-2 flex-wrap" style={{ flex: 1 }}>
        {/* Trigger progress */}
        <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg"
          style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
          <span className="text-xs font-mono font-bold" style={{ color: accentColor }}>{currentTrigger + 1}</span>
          <span className="text-xs font-mono" style={{ color: 'rgba(255,255,255,0.3)' }}>/</span>
          <span className="text-xs font-mono" style={{ color: 'rgba(255,255,255,0.5)' }}>{totalTriggers}</span>
        </div>

        {/* Hits */}
        <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg"
          style={{ background: 'rgba(0,230,118,0.08)', border: '1px solid rgba(0,230,118,0.2)' }}>
          <span className="text-xs font-mono" style={{ color: 'rgba(0,230,118,0.7)', fontSize: '9px', letterSpacing: '0.08em' }}>HIT</span>
          <span className="text-xs font-mono font-bold" style={{ color: '#00e676' }}>{hits.length}</span>
        </div>

        {/* Misses */}
        <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg"
          style={{ background: 'rgba(255,70,85,0.08)', border: '1px solid rgba(255,70,85,0.2)' }}>
          <span className="text-xs font-mono" style={{ color: 'rgba(255,70,85,0.7)', fontSize: '9px', letterSpacing: '0.08em' }}>MISS</span>
          <span className="text-xs font-mono font-bold" style={{ color: '#ff4655' }}>{misses.length}</span>
        </div>

        {/* Streak */}
        {streak > 1 && (
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg"
            style={{ background: hexToRgba(accentColor, 0.08), border: `1px solid ${hexToRgba(accentColor, 0.25)}` }}>
            <span className="text-xs font-mono" style={{ color: hexToRgba(accentColor, 0.7), fontSize: '9px', letterSpacing: '0.08em' }}>STREAK</span>
            <span className="text-xs font-mono font-bold" style={{ color: accentColor }}>{streak}×</span>
          </div>
        )}

        {/* Best ms */}
        {bestMs !== null && (
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg"
            style={{ background: 'rgba(255,214,0,0.06)', border: '1px solid rgba(255,214,0,0.18)' }}>
            <span className="text-xs font-mono" style={{ color: 'rgba(255,214,0,0.6)', fontSize: '9px', letterSpacing: '0.08em' }}>BEST</span>
            <span className="text-xs font-mono font-bold" style={{ color: '#ffd600' }}>{bestMs}ms</span>
          </div>
        )}

        {/* Avg ms */}
        {avgMs !== null && (
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg"
            style={{ background: hexToRgba(accentColor, 0.06), border: `1px solid ${hexToRgba(accentColor, 0.18)}` }}>
            <span className="text-xs font-mono" style={{ color: hexToRgba(accentColor, 0.6), fontSize: '9px', letterSpacing: '0.08em' }}>AVG</span>
            <span className="text-xs font-mono font-bold" style={{ color: accentColor }}>{avgMs}ms</span>
          </div>
        )}

        {/* Worst ms */}
        {worstMs !== null && (
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg"
            style={{ background: 'rgba(255,100,50,0.06)', border: '1px solid rgba(255,100,50,0.18)' }}>
            <span className="text-xs font-mono" style={{ color: 'rgba(255,100,50,0.6)', fontSize: '9px', letterSpacing: '0.08em' }}>WORST</span>
            <span className="text-xs font-mono font-bold" style={{ color: '#ff6432' }}>{worstMs}ms</span>
          </div>
        )}

        {/* Last shot */}
        {lastShot && lastShot.reactionMs !== null && (
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <span className="text-xs font-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: '9px', letterSpacing: '0.08em' }}>LAST</span>
            <span className="text-xs font-mono font-bold" style={{ color: outcomeColor(lastShot.outcome, accentColor) }}>
              {lastShot.reactionMs}ms
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Recent Shots List (left side) ─────────────────────────────────────────────

interface RecentShotsListProps {
  shots: ShotRecord[];
  accentColor: string;
}

function RecentShotsList({ shots, accentColor }: RecentShotsListProps) {
  const recent = shots.slice(-8).reverse();
  if (recent.length === 0) return null;

  return (
    <div
      className="absolute left-3 z-35 flex flex-col gap-1 pointer-events-none"
      style={{ top: '50%', transform: 'translateY(-50%)', maxWidth: 160 }}
    >
      <p className="text-xs font-mono uppercase tracking-widest mb-1" style={{ color: 'rgba(255,255,255,0.25)', fontSize: '8px' }}>Recent Shots</p>
      {recent.map((shot, i) => {
        const color = outcomeColor(shot.outcome, accentColor);
        const isLatest = i === 0;
        return (
          <div
            key={shot.id}
            className="flex items-center gap-1.5 px-2 py-1 rounded-lg"
            style={{
              background: isLatest ? hexToRgba(color, 0.12) : 'rgba(8,8,12,0.75)',
              border: `1px solid ${isLatest ? hexToRgba(color, 0.35) : 'rgba(255,255,255,0.07)'}`,
              opacity: 1 - i * 0.1,
              backdropFilter: 'blur(4px)',
            }}
          >
            <span className="text-xs font-mono font-bold" style={{ color, fontSize: '9px' }}>
              {outcomeLabel(shot.outcome)}
            </span>
            {shot.reactionMs !== null && (
              <span className="text-xs font-mono" style={{ color: 'rgba(255,255,255,0.5)', fontSize: '9px' }}>
                {shot.reactionMs}ms
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Clip List Panel ───────────────────────────────────────────────────────────

interface ClipListPanelProps {
  clips: ClipConfig[];
  currentIndex: number;
  onSelect: (idx: number) => void;
  settings: PlayerSettings;
  onSettingsChange: (s: PlayerSettings) => void;
  accentColor: string;
  mode: VideoReactMode;
}

function ClipListPanel({ clips, currentIndex, onSelect, settings, onSettingsChange, accentColor, mode }: ClipListPanelProps) {
  const isKillMode = mode === 'operator-react' || mode === 'kill-react';

  // Build grouped structure for kill mode
  const vandal = clips.filter(c => c.group === 'Vandal');
  const phantom = clips.filter(c => c.group === 'Phantom');

  const renderClipButton = (clip: ClipConfig, idx: number) => (
    <button key={clip.id} onClick={() => onSelect(idx)}
      className="w-full text-left rounded-lg px-3 py-2 text-xs font-mono transition-all"
      style={{
        background: idx === currentIndex ? `${accentColor}18` : 'rgba(255,255,255,0.04)',
        border: `1px solid ${idx === currentIndex ? `${accentColor}40` : 'rgba(255,255,255,0.06)'}`,
        color: idx === currentIndex ? accentColor : 'rgba(255,255,255,0.55)',
        cursor: 'pointer',
      }}>
      {clip.label}
      {clip.triggers.length > 1 && (
        <span className="ml-1.5 text-xs" style={{ color: 'rgba(255,255,255,0.3)', fontSize: '9px' }}>
          ×{clip.triggers.length}
        </span>
      )}
    </button>
  );

  const renderGroup = (label: string, groupClips: ClipConfig[]) => {
    if (groupClips.length === 0) return null;
    return (
      <div key={label} className="mb-3">
        <p className="text-xs font-mono uppercase tracking-widest mb-1.5 px-1" style={{ color: 'rgba(255,255,255,0.25)', fontSize: '8px' }}>{label}</p>
        <div className="flex flex-col gap-1">
          {groupClips.map(clip => {
            const idx = clips.findIndex(c => c.id === clip.id);
            return renderClipButton(clip, idx);
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full" style={{ background: 'rgba(10,10,15,0.55)', backdropFilter: 'blur(20px)', borderLeft: '1px solid rgba(255,255,255,0.06)' }}>
      <div className="flex-1 overflow-y-auto p-3 min-h-0">
        <p className="text-xs font-mono uppercase tracking-widest mb-2" style={{ color: 'rgba(255,255,255,0.3)', fontSize: '9px' }}>Clips</p>
        {clips.length === 0 ? (
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.2)' }}>No clips loaded</p>
        ) : isKillMode ? (
          <>
            {renderGroup('Vandal', vandal)}
            {renderGroup('Phantom', phantom)}
          </>
        ) : (
          <div className="flex flex-col gap-1">
            {clips.map((clip, idx) => renderClipButton(clip, idx))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main VideoReactPlayer ─────────────────────────────────────────────────────

export default function VideoReactPlayer({ mode, clips, onClose }: VideoReactPlayerProps) {
  const storageKey = `${SETTINGS_KEY_PREFIX}${mode}`;
  const accentColor = getModeAccent(mode);
  const modeLabel = getModeLabel(mode);
  const hasClips = clips.length > 0;

  const [settings, setSettings] = useState<PlayerSettings>(() => {
    if (typeof window === 'undefined') return DEFAULT_SETTINGS;
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? { ...DEFAULT_SETTINGS, ...JSON.parse(raw) } : DEFAULT_SETTINGS;
    } catch { return DEFAULT_SETTINGS; }
  });

  const [gameState, setGameState] = useState<'idle' | 'playing'>('idle');
  const [panelOpen, setPanelOpen] = useState(true);

  const [clipQueue, setClipQueue] = useState<number[]>([]);
  const [queuePos, setQueuePos] = useState(0);

  const [allShots, setAllShots] = useState<ShotRecord[]>([]);
  const shotCounterRef = useRef(0);

  const [currentTriggerIdx, setCurrentTriggerIdx] = useState(0);
  const [totalTriggers, setTotalTriggers] = useState(1);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const resolvedUrlCache = useRef<Map<string, string>>(new Map());
  const [videoLoading, setVideoLoading] = useState(false);

  const getVideoUrl = useCallback(async (src: string): Promise<string> => {
    if (resolvedUrlCache.current.has(src)) {
      return resolvedUrlCache.current.get(src)!;
    }
    const resolved = await resolveStreamableUrl(src);
    resolvedUrlCache.current.set(src, resolved);
    return resolved;
  }, []);

  const triggerArmedRef = useRef(false);
  const triggerFiredRef = useRef(false);
  const windowOpenRef = useRef(false);
  const triggerTimestampRef = useRef<number | null>(null);
  const windowCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentTriggerTimeRef = useRef<number>(0);
  const currentTriggerIdxRef = useRef<number>(0);
  const totalTriggersRef = useRef<number>(1);
  const clipQueueRef = useRef<number[]>([]);
  const queuePosRef = useRef<number>(0);
  const gameStateRef = useRef<'idle' | 'playing'>('idle');
  const multiTriggerResultsRef = useRef<TriggerResult[]>([]);

  // Shot already fired for current trigger window (prevent double-counting)
  const shotFiredThisWindowRef = useRef(false);

  useEffect(() => { clipQueueRef.current = clipQueue; }, [clipQueue]);
  useEffect(() => { queuePosRef.current = queuePos; }, [queuePos]);
  useEffect(() => { gameStateRef.current = gameState; }, [gameState]);

  useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify(settings)); } catch { /* ignore */ }
  }, [settings, storageKey]);

  const buildQueue = useCallback(() => {
    if (!hasClips) return [];
    const indices = clips.map((_, i) => i);
    if (settings.clipOrder === 'shuffled') {
      for (let i = indices.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [indices[i], indices[j]] = [indices[j], indices[i]];
      }
    }
    return indices;
  }, [clips, hasClips, settings.clipOrder]);

  const stopRaf = useCallback(() => {
    if (rafRef.current !== null) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
  }, []);

  const clearWindowTimer = useCallback(() => {
    if (windowCloseTimerRef.current) { clearTimeout(windowCloseTimerRef.current); windowCloseTimerRef.current = null; }
  }, []);

  const recordShot = useCallback((
    outcome: ReactionOutcome,
    reactionMs: number | null,
  ) => {
    const id = ++shotCounterRef.current;
    const shot: ShotRecord = { id, outcome, reactionMs, timestamp: performance.now() };
    setAllShots(prev => [...prev, shot]);
    return shot;
  }, []);

  // ── Advance to next clip ──────────────────────────────────────────────────

  const advanceToNextClip = useCallback((currentClips: ClipConfig[]) => {
    const nextPos = queuePosRef.current + 1;
    if (nextPos >= clipQueueRef.current.length) {
      const newQ = currentClips.map((_, i) => i);
      clipQueueRef.current = newQ;
      queuePosRef.current = 0;
      setClipQueue(newQ);
      setQueuePos(0);
    } else {
      queuePosRef.current = nextPos;
      setQueuePos(nextPos);
    }
  }, []);

  // ── Arm next trigger (rAF loop) ───────────────────────────────────────────

  const armNextTrigger = useCallback((clipIdx: number, trigIdx: number, totalTrig: number) => {
    const clip = clips[clipIdx];
    if (!clip) return;

    const triggerTimeSec = (() => {
      const overrides = settings.triggerOverrides[clip.id];
      if (overrides && overrides[trigIdx] !== undefined) return overrides[trigIdx];
      return clip.triggers[trigIdx]?.triggerTime ?? 0;
    })();

    currentTriggerIdxRef.current = trigIdx;
    totalTriggersRef.current = totalTrig;
    currentTriggerTimeRef.current = triggerTimeSec;
    triggerArmedRef.current = true;
    triggerFiredRef.current = false;
    windowOpenRef.current = false;
    triggerTimestampRef.current = null;
    shotFiredThisWindowRef.current = false;

    setCurrentTriggerIdx(trigIdx);
    setTotalTriggers(totalTrig);

    const poll = () => {
      if (!videoRef.current || gameStateRef.current !== 'playing') return;
      const ct = videoRef.current.currentTime;
      if (!triggerFiredRef.current && ct >= triggerTimeSec) {
        triggerFiredRef.current = true;
        triggerTimestampRef.current = performance.now();
        windowOpenRef.current = true;

        // When the reaction window expires, just close the window — do NOT record any shot.
        // A shot is ONLY recorded when the user actually clicks.
        windowCloseTimerRef.current = setTimeout(() => {
          if (windowOpenRef.current) {
            windowOpenRef.current = false;
            shotFiredThisWindowRef.current = false;

            // Advance to the next trigger (still no shot recorded)
            const nextIdx = trigIdx + 1;
            if (nextIdx < totalTrig) {
              const cIdx = clipQueueRef.current[queuePosRef.current] ?? 0;
              armNextTrigger(cIdx, nextIdx, totalTrig);
            }
            // If this was the last trigger, just let the video keep playing to its end.
            // The onEnded handler will advance to the next clip.
          }
        }, REACTION_WINDOW_MS);

        // Trigger has fired — stop polling, the timeout handles the rest
        return;
      }
      rafRef.current = requestAnimationFrame(poll);
    };
    rafRef.current = requestAnimationFrame(poll);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clips, settings.triggerOverrides, recordShot]);

  // ── Handle click ──────────────────────────────────────────────────────────

  const handleClick = useCallback(() => {
    if (gameStateRef.current !== 'playing') return;
    const now = performance.now();

    // Click before trigger fires → Too Early
    if (!triggerFiredRef.current) {
      clearWindowTimer();
      const tIdx = currentTriggerIdxRef.current;
      const totalTrig = totalTriggersRef.current;
      recordShot('too-early', null);
      multiTriggerResultsRef.current.push({ triggerIndex: tIdx, outcome: 'too-early', reactionMs: null });
      windowOpenRef.current = false;
      shotFiredThisWindowRef.current = false;

      const nextIdx = tIdx + 1;
      if (nextIdx < totalTrig) {
        const cIdx = clipQueueRef.current[queuePosRef.current] ?? 0;
        armNextTrigger(cIdx, nextIdx, totalTrig);
      }
      // If last trigger, let video play to end — onEnded will advance
      return;
    }

    // Trigger window is open — record timed shot (only first click per window)
    if (windowOpenRef.current && triggerTimestampRef.current !== null && !shotFiredThisWindowRef.current) {
      shotFiredThisWindowRef.current = true;
      const reactionMs = Math.round(now - triggerTimestampRef.current);
      const outcome = classifyReaction(reactionMs);
      recordShot(outcome, reactionMs);

      clearWindowTimer();
      windowOpenRef.current = false;
      const tIdx = currentTriggerIdxRef.current;
      const totalTrig = totalTriggersRef.current;
      multiTriggerResultsRef.current.push({ triggerIndex: tIdx, outcome, reactionMs });

      const nextIdx = tIdx + 1;
      if (nextIdx < totalTrig) {
        const cIdx = clipQueueRef.current[queuePosRef.current] ?? 0;
        armNextTrigger(cIdx, nextIdx, totalTrig);
      }
      // If last trigger, let video play to end — onEnded will advance
      return;
    }

    // Trigger fired but window already closed → Late click
    if (triggerFiredRef.current && !windowOpenRef.current && !shotFiredThisWindowRef.current) {
      shotFiredThisWindowRef.current = true;
      recordShot('too-late', null);
      multiTriggerResultsRef.current.push({ triggerIndex: currentTriggerIdxRef.current, outcome: 'too-late', reactionMs: null });
    }
  }, [clearWindowTimer, recordShot, armNextTrigger, clips]);

  const loadAndPlayClip = useCallback(async (clip: ClipConfig, clipIdx: number) => {
    stopRaf();
    clearWindowTimer();
    triggerArmedRef.current = false;
    triggerFiredRef.current = false;
    windowOpenRef.current = false;
    multiTriggerResultsRef.current = [];
    shotFiredThisWindowRef.current = false;

    setVideoLoading(true);
    const resolvedSrc = await getVideoUrl(clip.src);
    setVideoLoading(false);

    if (videoRef.current) {
      videoRef.current.src = resolvedSrc;
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(() => {});
    }
    armNextTrigger(clipIdx, 0, clip.triggers.length);
  }, [stopRaf, clearWindowTimer, armNextTrigger, getVideoUrl]);

  const startGame = useCallback(() => {
    if (!hasClips) return;
    const queue = buildQueue();
    const pos = 0;
    setClipQueue(queue);
    setQueuePos(pos);
    clipQueueRef.current = queue;
    queuePosRef.current = pos;
    setAllShots([]);
    multiTriggerResultsRef.current = [];
    setGameState('playing');
    gameStateRef.current = 'playing';
    const clip = clips[queue[pos]];
    if (clip) loadAndPlayClip(clip, queue[pos]);
  }, [hasClips, buildQueue, clips, loadAndPlayClip]);

  const jumpToClip = useCallback((idx: number) => {
    stopRaf();
    clearWindowTimer();
    multiTriggerResultsRef.current = [];
    setGameState('playing');
    gameStateRef.current = 'playing';
    const newQueue = clips.map((_, i) => i);
    setClipQueue(newQueue);
    setQueuePos(idx);
    clipQueueRef.current = newQueue;
    queuePosRef.current = idx;
    const clip = clips[idx];
    if (clip) loadAndPlayClip(clip, idx);
  }, [clips, stopRaf, clearWindowTimer, loadAndPlayClip]);

  // ── Next clip button ──────────────────────────────────────────────────────

  const goNextClip = useCallback(() => {
    if (!hasClips) return;
    stopRaf();
    clearWindowTimer();
    multiTriggerResultsRef.current = [];

    const currentQueue = clipQueueRef.current.length > 0 ? clipQueueRef.current : clips.map((_, i) => i);
    const nextPos = (queuePosRef.current + 1) % currentQueue.length;

    clipQueueRef.current = currentQueue;
    queuePosRef.current = nextPos;
    setClipQueue([...currentQueue]);
    setQueuePos(nextPos);

    if (gameStateRef.current !== 'playing') {
      setGameState('playing');
      gameStateRef.current = 'playing';
    }

    const clip = clips[currentQueue[nextPos]];
    if (clip) loadAndPlayClip(clip, currentQueue[nextPos]);
  }, [hasClips, clips, stopRaf, clearWindowTimer, loadAndPlayClip]);

  const stopGame = useCallback(() => {
    stopRaf();
    clearWindowTimer();
    setGameState('idle');
    gameStateRef.current = 'idle';
    if (videoRef.current) { videoRef.current.pause(); videoRef.current.currentTime = 0; }
    if (onClose) onClose();
  }, [stopRaf, clearWindowTimer, onClose]);

  useEffect(() => {
    return () => { stopRaf(); clearWindowTimer(); };
  }, [stopRaf, clearWindowTimer]);

  const currentClipArrayIdx = clipQueue[queuePos] ?? 0;

  // Compute stats for header display
  const hitShots = allShots.filter(s => s.outcome !== 'too-early' && s.outcome !== 'too-late' && s.reactionMs !== null);
  const reactionTimes = hitShots.map(s => s.reactionMs as number);
  const headerBest = reactionTimes.length > 0 ? Math.min(...reactionTimes) : null;
  const headerAvg = reactionTimes.length > 0 ? Math.round(reactionTimes.reduce((a, b) => a + b, 0) / reactionTimes.length) : null;
  const headerWorst = reactionTimes.length > 0 ? Math.max(...reactionTimes) : null;

  return (
    <div
      className="relative w-full h-screen overflow-hidden flex flex-col"
      style={{ background: '#0a0a0a', fontFamily: 'var(--font-geist), sans-serif' }}
      onMouseDown={gameState === 'playing' ? handleClick : undefined}
    >
      {/* ── Top Stats Bar ── */}
      {gameState === 'playing' && (
        <StatsBar
          shots={allShots}
          accentColor={accentColor}
          totalTriggers={totalTriggers}
          currentTrigger={currentTriggerIdx}
        />
      )}

      {/* ── Top nav bar ── */}
      <div
        className="absolute z-40 flex items-center justify-between px-3 pointer-events-auto"
        style={{
          top: gameState === 'playing' ? 48 : 0,
          left: 0,
          right: 0,
          paddingTop: 8,
          paddingBottom: 8,
          background: gameState === 'playing' ? 'transparent' : 'linear-gradient(to bottom, rgba(10,10,15,0.75) 0%, rgba(10,10,15,0) 100%)',
        }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ff4655" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="1" fill="#ff4655" />
              <line x1="12" y1="3" x2="12" y2="6" /><line x1="12" y1="18" x2="12" y2="21" />
              <line x1="3" y1="12" x2="6" y2="12" /><line x1="18" y1="12" x2="21" y2="12" />
            </svg>
            <span className="text-sm font-bold tracking-tight text-white" style={{ letterSpacing: '-0.01em' }}>
              fps<span style={{ color: '#ff4655' }}>.</span>expert
            </span>
          </div>
          <Link href="/"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-mono font-bold tracking-widest uppercase transition-colors"
            style={{ color: 'rgba(255,255,255,0.6)', background: 'rgba(10,10,15,0.5)', border: '1px solid rgba(255,255,255,0.12)', textDecoration: 'none', backdropFilter: 'blur(8px)' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(255,255,255,0.9)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(255,255,255,0.6)'; }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 5l-7 7 7 7" />
            </svg>
            Menu
          </Link>
        </div>

        {/* Center: stats or mode label */}
        <div className="absolute left-1/2 top-1/2 flex items-center gap-3" style={{ transform: 'translate(-50%, -50%)', pointerEvents: 'none' }}>
          {reactionTimes.length > 0 ? (
            <>
              {headerBest !== null && (
                <div className="flex items-center gap-1">
                  <span className="text-xs font-mono" style={{ color: 'rgba(255,214,0,0.6)', fontSize: '9px' }}>BEST</span>
                  <span className="text-xs font-mono font-bold" style={{ color: '#ffd600' }}>{headerBest}ms</span>
                </div>
              )}
              <div style={{ width: 1, height: 12, background: 'rgba(255,255,255,0.15)' }} />
              {headerAvg !== null && (
                <div className="flex items-center gap-1">
                  <span className="text-xs font-mono" style={{ color: hexToRgba(accentColor, 0.6), fontSize: '9px' }}>AVG</span>
                  <span className="text-xs font-mono font-bold" style={{ color: accentColor }}>{headerAvg}ms</span>
                </div>
              )}
              <div style={{ width: 1, height: 12, background: 'rgba(255,255,255,0.15)' }} />
              {headerWorst !== null && (
                <div className="flex items-center gap-1">
                  <span className="text-xs font-mono" style={{ color: 'rgba(255,100,50,0.6)', fontSize: '9px' }}>WORST</span>
                  <span className="text-xs font-mono font-bold" style={{ color: '#ff6432' }}>{headerWorst}ms</span>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="w-1.5 h-1.5 rounded-full" style={{ background: accentColor, boxShadow: `0 0 6px ${accentColor}` }} />
              <span className="text-xs font-mono font-bold uppercase tracking-widest" style={{ color: accentColor, textShadow: `0 0 12px ${accentColor}88` }}>{modeLabel}</span>
            </>
          )}
        </div>

      </div>

      {/* ── Main area ── */}
      <div className="absolute inset-0 flex overflow-hidden">
        <div
          className="relative flex-1 overflow-hidden"
          style={{ cursor: gameState === 'playing' ? 'crosshair' : 'default' }}
          onClick={gameState === 'idle' ? startGame : undefined}
        >
          {/* No clips placeholder */}
          {!hasClips && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
              <div className="absolute inset-0 opacity-[0.03]"
                style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
              <div className="relative flex flex-col items-center gap-5 rounded-2xl px-10 py-10 text-center"
                style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${accentColor}22`, maxWidth: 420 }}>
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl"
                  style={{ background: `${accentColor}12`, border: `1px solid ${accentColor}25` }}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={accentColor} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.8 }}>
                    <path d="M15 10l4.553-2.069A1 1 0 0121 8.87v6.26a1 1 0 01-1.447.899L15 14" />
                    <rect x="3" y="6" width="12" height="12" rx="2" />
                  </svg>
                </div>
                <div>
                  <p className="mb-1 text-xs font-semibold uppercase tracking-widest" style={{ color: accentColor, opacity: 0.8 }}>{modeLabel}</p>
                  <h2 className="mb-2 text-xl font-bold text-white" style={{ letterSpacing: '-0.02em' }}>No clips loaded</h2>
                  <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.38)' }}>
                    Video clips haven&apos;t been imported yet. Add entries to the clip config to enable gameplay.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Click-to-begin overlay */}
          {hasClips && gameState === 'idle' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-6 z-20" style={{ background: 'rgba(0,0,0,0.72)' }}>
              <div className="flex flex-col items-center gap-4 text-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl"
                  style={{ background: `${accentColor}15`, border: `1px solid ${accentColor}30` }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={accentColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="5 3 19 12 5 21 5 3" fill={accentColor} fillOpacity="0.2" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs font-mono uppercase tracking-widest mb-1" style={{ color: `${accentColor}99` }}>{modeLabel}</p>
                  <h2 className="text-2xl font-bold text-white mb-2" style={{ letterSpacing: '-0.02em' }}>Ready to train</h2>
                  <p className="text-sm" style={{ color: 'rgba(255,255,255,0.4)' }}>Click anywhere to begin</p>
                </div>
                <div className="mt-2 px-6 py-2.5 rounded-xl text-sm font-bold uppercase tracking-widest"
                  style={{ background: accentColor, color: '#fff', letterSpacing: '0.1em', boxShadow: `0 0 20px ${accentColor}55` }}>
                  Press Start
                </div>
              </div>
            </div>
          )}

          {/* Video element */}
          <video
            ref={videoRef}
            className="absolute inset-0 w-full h-full object-contain"
            playsInline
            preload="auto"
            style={{ display: gameState === 'playing' ? 'block' : 'none' }}
            onEnded={() => {
              if (gameStateRef.current !== 'playing') return;
              stopRaf();
              clearWindowTimer();
              multiTriggerResultsRef.current = [];
              const currentQueue = clipQueueRef.current.length > 0 ? clipQueueRef.current : clips.map((_, i) => i);
              const nextPos = (queuePosRef.current + 1) % currentQueue.length;
              clipQueueRef.current = currentQueue;
              queuePosRef.current = nextPos;
              setClipQueue([...currentQueue]);
              setQueuePos(nextPos);
              const nextClip = clips[currentQueue[nextPos]];
              if (nextClip) loadAndPlayClip(nextClip, currentQueue[nextPos]);
            }}
          />

          {/* Loading indicator */}
          {videoLoading && gameState === 'playing' && (
            <div className="absolute inset-0 flex items-center justify-center z-30" style={{ background: 'rgba(0,0,0,0.6)' }}>
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: `${accentColor} transparent transparent transparent` }} />
                <span className="text-xs font-mono" style={{ color: 'rgba(255,255,255,0.5)' }}>Loading clip…</span>
              </div>
            </div>
          )}

          {/* Recent shots list */}
          {gameState === 'playing' && (
            <RecentShotsList shots={allShots} accentColor={accentColor} />
          )}
        </div>

        {/* Right panel */}
        {panelOpen && (
          <div
            className="absolute right-0 bottom-0 w-48 flex-shrink-0 flex flex-col z-30"
            style={{ top: gameState === 'playing' ? 96 : 48 }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <ClipListPanel
              clips={clips}
              currentIndex={currentClipArrayIdx}
              onSelect={jumpToClip}
              settings={settings}
              onSettingsChange={setSettings}
              accentColor={accentColor}
              mode={mode}
            />
          </div>
        )}
      </div>

      {/* Bottom bar */}
      <div
        className="absolute bottom-0 left-0 right-0 z-40 flex items-center px-4 py-3"
        style={{ background: 'linear-gradient(to top, rgba(10,10,15,0.75) 0%, rgba(10,10,15,0) 100%)' }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Bottom-left: Panel/Hide button */}
        <button onClick={() => setPanelOpen((v) => !v)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-mono uppercase tracking-widest transition-all"
          style={{ background: panelOpen ? hexToRgba(accentColor, 0.15) : 'rgba(10,10,15,0.5)', border: `1px solid ${panelOpen ? hexToRgba(accentColor, 0.35) : 'rgba(255,255,255,0.12)'}`, color: panelOpen ? accentColor : 'rgba(255,255,255,0.6)', cursor: 'pointer', backdropFilter: 'blur(8px)' }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" /><line x1="9" y1="3" x2="9" y2="21" />
          </svg>
          {panelOpen ? 'Hide' : 'Panel'}
        </button>

        <div className="flex items-center gap-3 absolute left-1/2 -translate-x-1/2">
          {/* Shot count */}
          {gameState === 'playing' && allShots.length > 0 && (
            <>
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-mono" style={{ color: 'rgba(255,255,255,0.35)', fontSize: '10px' }}>Shots</span>
                <span className="text-xs font-mono font-bold" style={{ color: 'rgba(255,255,255,0.7)' }}>{allShots.length}</span>
              </div>
              <div style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.15)' }} />
            </>
          )}

          {/* Next button */}
          <button
            onClick={goNextClip}
            onMouseDown={(e) => e.stopPropagation()}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-widest transition-all"
            style={{
              background: hexToRgba(accentColor, 0.1),
              border: `1px solid ${hexToRgba(accentColor, 0.3)}`,
              color: hexToRgba(accentColor, 0.8),
              cursor: 'pointer', letterSpacing: '0.1em', backdropFilter: 'blur(8px)',
            }}>
            Next
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>

          {/* Play/Stop */}
          <button
            onClick={() => {
              if (gameState === 'idle') startGame();
              else stopGame();
            }}
            onMouseDown={(e) => e.stopPropagation()}
            className="flex items-center gap-2 px-5 py-2 rounded-xl text-xs font-bold uppercase tracking-widest transition-all"
            style={{
              background: gameState !== 'idle' ? 'rgba(255,70,85,0.2)' : hexToRgba(accentColor, 0.22),
              border: `1px solid ${gameState !== 'idle' ? 'rgba(255,70,85,0.5)' : hexToRgba(accentColor, 0.5)}`,
              color: gameState !== 'idle' ? '#ff4655' : accentColor,
              cursor: 'pointer', letterSpacing: '0.1em', backdropFilter: 'blur(8px)',
            }}>
            {gameState !== 'idle' ? (
              <>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" /></svg>
                Stop
              </>
            ) : (
              <>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3" /></svg>
                Play
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
